import { isEthereumLikeAsset } from '@exodus/ethereum-lib';
import RPC from '@exodus/json-rpc';
import {
  createEVMDepsFactory,
  registerRPCHandlers,
} from '@exodus/web3-ethereum/rpc';
import type { EthWalletWatchAsset } from '@exodus/web3-ethereum-utils';
import { createSimulationServices } from '@exodus/web3-simulation';
import type { EthereumLikeAsset } from '@passkeys/assets';

import type { Asset } from '@/types';
import { createAppDeps, createSignerDeps, createWalletDeps } from './deps.js';
import { SIMULATION_API_URL } from '../constants.js';
import type { Web3Dependencies } from '../types.js';
import { fakeAnalytics } from '../utils/analytics.js';
import { createTransport } from '../utils/transport.js';

type EthereumDependecies = Parameters<typeof registerRPCHandlers>[0]['deps'];

type EVMDeps = EthereumDependecies['evms'][0];

const createEthereumDeps = (exodus: Web3Dependencies): EthereumDependecies => {
  const allAssets = exodus.assetsModule.getAssets();
  const evmAssets: EthereumLikeAsset[] =
    Object.values(allAssets).filter(isEthereumLikeAsset);

  const supportedChainIds: string[] = evmAssets.map(
    (asset) => `0x${asset.chainId!.toString(16)}`,
  );

  const createEVMDeps = createEVMDepsFactory({
    analytics: fakeAnalytics,
    blockchainMetadata: exodus.blockchainMetadata,
  });

  const getAsset = (assetName: string) =>
    exodus.assetsModule.getAsset(assetName);

  const getActiveWalletAccount = () => exodus.walletAccounts.getActive();

  const getActiveWalletAccountData = async () => {
    const walletAccounts = await exodus.walletAccounts.getEnabled();
    const activeWalletAccount = await exodus.walletAccounts.getActive();
    return walletAccounts[activeWalletAccount];
  };

  const getSupportedChainIds = async () => supportedChainIds;

  const getAddress = async (assetName: string) => {
    const walletAccount = await getActiveWalletAccountData();
    const { address } = await exodus.addressProvider.getReceiveAddress({
      assetName,
      walletAccount,
    });
    return address;
  };

  const getFeeData = ({ assetName }: { assetName: string }) =>
    exodus.feesApi.fees.getFeeData({ assetName });

  const addEthereumChain = async ({ chainId }: { chainId: string }) => {
    const isChainSupported = supportedChainIds.find(
      (supportedChainId) => supportedChainId === chainId,
    );

    if (isChainSupported) {
      // Returning null accordingly to https://eips.ethereum.org/EIPS/eip-3085.
      return null;
    }

    // The EIP does not specify which error should be returned.
    throw new Error('This chain can not be added.');
  };

  const evmDeps: EVMDeps[] = [];
  evmAssets.forEach((asset) => {
    const assetName = asset.name;
    const chainId = `0x${asset.chainId?.toString(16)}`;

    const getBaseAssetName = ({
      chainId: assetChainId,
    }: Pick<Asset, 'chainId'>) =>
      assetChainId
        ? evmAssets.find((asset) => asset.chainId === assetChainId)?.name
        : assetName;

    const { transactionSigner, messageSigner } = createSignerDeps({
      assetName,
      baseAssetName: getBaseAssetName(asset),
      transactionSigner: exodus.transactionSigner,
      messageSigner: exodus.messageSigner,
      analytics: exodus.analytics,
    });

    const onEthWalletWatchAssetRequest = async (args: EthWalletWatchAsset) => {
      const { chainId: assetChainId, address: assetAddress } = args.options;

      const baseAssetName = getBaseAssetName({ chainId: assetChainId });
      if (!baseAssetName) {
        return false;
      }

      try {
        await exodus.enabledAssetsApi.assets.addAndEnableToken(
          assetAddress,
          baseAssetName,
        );
      } catch (error) {
        console.error(error);
        return false;
      }

      return true;
    };

    const { simulateEthereumTransactions } = createSimulationServices({
      apiBaseURL: SIMULATION_API_URL,
      assetClientInterface: exodus.assetClientInterface,
    });

    const deps = createEVMDeps({
      addEthereumChain,
      assetName,
      chainId,
      origin: '', // Only used by `analytics.track()` which is mocked.
      getAsset,
      getActiveWalletAccount,
      getActiveWalletAccountData,
      getSupportedChainIds,
      getAddress,
      getFeeData,
      simulateEthereumTransactions,
      transactionSigner,
      messageSigner,
      onEthWalletWatchAssetRequest,
    });

    evmDeps.push(deps);
  });

  return {
    evms: evmDeps,
    app: createAppDeps(exodus),
    wallet: createWalletDeps(exodus),
  };
};

export const createEthereumRequestHandler = (exodus: Web3Dependencies) => {
  const transport = createTransport();

  const deps = createEthereumDeps(exodus);

  registerRPCHandlers({ deps, transport, RPC });

  return (request: unknown) => {
    transport.emit('data', request);
  };
};
